import React, { useState } from 'react';
import authedFetch from '../../lib/authedFetch';
import secureStorage from '../../lib/secureStorage';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { toast } from 'sonner';
import { Check, ArrowRight, ChevronDown } from 'lucide-react';
import { MODE_META } from './LeiModeControls';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact LEI Scoring Mode control that lives on the right edge of the
 * page sub-nav. Shows the current mode as a pill; clicking opens a popover
 * with the read-only preview + org-wide switch (admins only).
 */
export const LeiModePopoverButton = ({ currentMode = 'standalone', selectedOrg, isAdmin = false, onCommitted }) => {
  const [open, setOpen] = useState(false);
  const [cmp, setCmp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const meta = MODE_META[currentMode] || MODE_META.standalone;
  const ModeIcon = meta.icon;
  const orgParam = selectedOrg && selectedOrg !== 'all' ? `&organization_id=${selectedOrg}` : '';
  const token = secureStorage.getItem('session_token');

  const loadCompare = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        authedFetch(`${API_URL}/api/lei-health/overview?preview_mode=standalone${orgParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        authedFetch(`${API_URL}/api/lei-health/overview?preview_mode=conjoined${orgParam}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setCmp({ standalone: await a.json(), conjoined: await b.json() });
    } catch (e) {
      toast.error('Could not load mode comparison');
    } finally {
      setLoading(false);
    }
  };

  const onOpenChange = (next) => {
    setOpen(next);
    if (next && isAdmin && !cmp) loadCompare();
  };

  const commit = async (mode) => {
    setCommitting(true);
    try {
      const res = await authedFetch(`${API_URL}/api/modelling-settings/lei-mode`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lei_mode: mode, ...(selectedOrg && selectedOrg !== 'all' ? { organization_id: selectedOrg } : {}) }),
      });
      if (res.ok) {
        toast.success(`LEI scoring set to "${MODE_META[mode].label}" org-wide`);
        setCmp(null);
        setOpen(false);
        onCommitted?.(mode);
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Could not update mode');
      }
    } finally {
      setCommitting(false);
    }
  };

  const scoreOf = (m) => (cmp?.[m]?.overall_score != null ? Number(cmp[m].overall_score).toFixed(1) : '—');

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-testid="lei-mode-popover-trigger"
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#22304d] text-slate-300 hover:border-[#44b6d2] hover:text-slate-100 transition-colors"
        >
          <ModeIcon size={13} style={{ color: meta.color }} />
          <span className="text-slate-400">LEI Mode:</span>
          <span style={{ color: meta.color }}>{meta.label}</span>
          <ChevronDown size={13} className="text-slate-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-4" data-testid="lei-mode-popover">
        <div className="flex items-center gap-2 mb-1">
          <ModeIcon size={15} style={{ color: meta.color }} />
          <span className="text-sm font-semibold text-slate-700">LEI Scoring Mode</span>
          <Badge style={{ backgroundColor: meta.color + '18', color: meta.color, borderColor: meta.color + '40' }} className="border">{meta.label}</Badge>
        </div>
        <p className="text-xs text-slate-500 mb-3">{meta.desc}</p>

        {isAdmin ? (
          <div className="border-t pt-3" data-testid="lei-mode-popover-compare">
            <p className="text-[11px] text-slate-500 mb-2">Read-only preview of the headline LEI under each mode. Committing changes how LEI is scored org-wide.</p>
            <div className="grid grid-cols-2 gap-2">
              {['standalone', 'conjoined'].map((m) => {
                const mm = MODE_META[m];
                const active = currentMode === m;
                return (
                  <div key={m} className={`rounded-lg border p-2.5 ${active ? 'ring-2' : ''}`} style={active ? { borderColor: mm.color, boxShadow: `0 0 0 1px ${mm.color}` } : {}} data-testid={`lei-mode-popover-${m}`}>
                    <div className="flex items-start justify-between gap-1">
                      <span className="text-[11px] font-medium text-slate-600 leading-tight">{mm.label}</span>
                      {active && <Badge variant="secondary" className="text-[9px] shrink-0 ml-1">Current</Badge>}
                    </div>
                    <div className="mt-1 flex items-baseline gap-1">
                      <span className="text-2xl font-bold" style={{ color: mm.color }} data-testid={`lei-mode-popover-score-${m}`}>{loading ? '…' : scoreOf(m)}</span>
                      <span className="text-[10px] text-slate-400">LEI</span>
                    </div>
                    <Button size="sm" className="w-full mt-2 gap-1 h-7 text-[11px]" variant={active ? 'secondary' : 'default'} disabled={active || committing || loading} onClick={() => commit(m)} data-testid={`lei-mode-popover-apply-${m}`}>
                      {active ? <><Check size={12} /> In use</> : <>Apply <ArrowRight size={12} /></>}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-[11px] text-slate-400 border-t pt-3">Only administrators can change the scoring mode.</p>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default LeiModePopoverButton;
